import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { useMultiStepForm } from "@/hooks";
import { cn } from "@/utils/tailwind";

const FormStepper: FC = () => {
  const { t } = useTranslation();
  const { currentStepIndex, steps } = useMultiStepForm();

  return (
    <div
      className="flex w-full items-center justify-between sm:pb-14 md:pb-10 lg:h-fit lg:w-fit lg:min-w-[305px] lg:flex-col lg:items-start lg:pb-0"
      data-test="form-stepper"
    >
      {steps.map((step, index) => {
        const stepNumber = index;
        const isActive = currentStepIndex >= stepNumber;
        const isCurrent = currentStepIndex === stepNumber;
        const isLast = index === steps.length - 1;

        return (
          <div
            key={step.title}
            className={cn(
              "flex items-center",
              !isLast && "flex-1 lg:h-auto lg:flex-none",
              "lg:flex lg:items-start lg:gap-4",
            )}
          >
            <div className="relative flex flex-1 flex-col items-start lg:h-[90px] lg:flex-row lg:items-start">
              <div
                className={cn(
                  "text-background relative z-10 flex size-10 items-center justify-center rounded-full text-[11px] font-bold sm:size-[50px] sm:text-[13px]",
                  isActive ? "bg-primary" : "bg-accent",
                )}
                data-test="step-number"
              >
                {String(stepNumber + 1).padStart(2, "0")}
                <div
                  className={cn(
                    "text-foreground absolute top-[60px] bottom-0 left-0 hidden w-[100px] text-xs font-medium sm:block md:w-[120px] lg:hidden",
                    isLast && "w-full text-center md:w-full",
                  )}
                  data-test="desktop-step-title"
                >
                  {t(`stepper.${step.title}`)}
                </div>
              </div>

              {!isLast && (
                <div
                  className={cn(
                    "bg-accent absolute top-[17px] sm:top-[22px]",
                    "h-1.5 w-full",
                    "lg:absolute lg:top-auto lg:left-[22px] lg:h-[90px] lg:w-1.5",
                    isActive && !isCurrent && "bg-primary",
                  )}
                />
              )}
            </div>

            <div
              className={cn(
                "text-foreground mt-2 hidden text-center font-medium sm:text-xs md:text-sm lg:block",
                "lg:mt-0 lg:block lg:text-left lg:text-base lg:leading-[50px]",
              )}
              data-test="mobile-step-title"
            >
              {t(`stepper.${step.title}`)}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default FormStepper;
